import { useState, useLayoutEffect } from 'react';

/* THE PARTNER'S SKIN — one colour in, every token out.

   The React port of SP-main's `SF.applyTheme()` and the contrast helpers it
   leans on. Static tokens live in `src/shared/styles/storefront-tokens.css`;
   this file writes the handful that depend on what the partner saved.

   We own the anatomy, the partner owns the skin: a customer-facing page must
   never hardcode a colour or headline font one of these tokens supplies.

   The ink is solved, never chosen — `--o-ink` and `--m-ink` are derived by
   measuring contrast against their surface, after a partner was able to publish
   an Add-to-cart button at 1.68:1. */

/* The warm near-black. ⛔ Not pure black: #000 on #fff is 21:1, a known cause of
   halation for readers with astigmatism and dyslexic readers. #241f1d is
   16.29:1. */
const STOREFRONT_DARK = '#241f1d';
const STOREFRONT_INK = '#241f1d';

/* Above this ratio the white is dimmed toward the surface. WCAG sets a floor and
   no ceiling, so this is not a compliance fix; it has to be thresholded, because
   dimming the ink on every dark surface drops the pale end of the palette under
   AA (Clay #a4562a falls to 4.46:1). At 12 the rule reaches only the
   near-black. */
const STOREFRONT_ON_DARK_MAX = 12;
const ON_DARK_MIX = 0.92;

/* ⚠ WHITE WINS TIES ON PURPOSE. The Style tab's 12 swatches (StorefrontStyleTab
   in the designer) are all deep, curated colours by design — every one of them
   clears 5.3:1 with white, comfortably above AA. The only surfaces that ever
   drop below straight white-vs-ink comparison are pre-redesign accents no
   longer offered in the picker (e.g. the old "Deep Tangerine" #E15439, which
   scores white 3.80 / ink 4.29 — dark technically "wins" by a hair, but reads
   worse against a saturated orange than the ratio suggests, and is not a
   colour a partner can choose today). Below this floor white still yields to
   the near-black ink; at or above it, white is kept even if the near-black
   would score marginally higher. */
const MIN_WHITE_CONTRAST = 3;

const normalizeHex = (hex) => {
  const h = String(hex || '').replace('#', '');
  if (h.length === 3) return h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return h;
};

const relativeLuminance = (hex) => {
  const h = normalizeHex(hex);
  if (!/^[0-9a-f]{6}$/i.test(h)) return 0;
  const c = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

export const contrastRatio = (a, b) => {
  let l1 = relativeLuminance(a);
  let l2 = relativeLuminance(b);
  if (l1 < l2) { const t = l1; l1 = l2; l2 = t; }
  return (l1 + 0.05) / (l2 + 0.05);
};

/* White at `amt` over the colour itself, so a dimmed ink is in-family by
   construction rather than a second constant to maintain. */
const mixToward = (hex, amt) => {
  const h = normalizeHex(hex);
  if (!/^[0-9a-f]{6}$/i.test(h)) return '#ffffff';
  const mix = (i) =>
    Math.round(255 * amt + parseInt(h.slice(i, i + 2), 16) * (1 - amt))
      .toString(16)
      .padStart(2, '0');
  return `#${mix(0)}${mix(2)}${mix(4)}`;
};

/** Given a surface, the readable ink for it — white unless it can't clear the
 *  floor on this surface, in which case the warm near-black, with the
 *  very-dark case dimmed. */
export const readableOn = (bg) => {
  const onWhite = contrastRatio(bg, '#ffffff');
  if (onWhite < MIN_WHITE_CONTRAST) return STOREFRONT_INK;
  return onWhite > STOREFRONT_ON_DARK_MAX ? mixToward(bg, ON_DARK_MIX) : '#ffffff';
};

/* ⛔ Not `readableOn(bg) === '#ffffff'`: readableOn returns a DIMMED white on a
   very dark surface, so that string test calls the near-black light — flipping
   the banner's ink class and dropping the header's edge. The honest test is "it
   did not ask for the dark ink". */
export const isDarkColor = (bg) => readableOn(bg) !== STOREFRONT_INK;

/* Light storefront colors → darker tone for links and button edges on white (portal OnboardingFlow/constants.js). */
const LIGHT_TONES = {
  '#ead9bd': '#9c6916', // Sand
  '#d0d0d0': '#585858', // Light charcoal
  '#d6e4f2': '#1d74cc', // Sky
  '#dce6d7': '#547e3f', // Sage mist
  '#e6dbf4': '#8b60c3', // Lavender
  '#f6ddd8': '#b8402a', // Blush
};

/** The darker tone of a light storefront color, or undefined for a dark one. */
export const lightToneOf = (hex) => LIGHT_TONES[String(hex || '').trim().toLowerCase()];

/** Retired colors still saved on some stores → the color that replaced them. */
const RETIRED_COLORS = { '#f2e9d8': '#ead9bd' }; // Cream → Sand

export const currentColorOf = (hex) => RETIRED_COLORS[String(hex || '').trim().toLowerCase()] || hex;

/** Announcement bar colors: the store color when "Match my style color" is on, else Light (default) or Near-black. */
export const announcementBarColors = (config) => {
  const cfg = config || {};
  if (cfg.announcementMatch === true) {
    const bg = getAccent(cfg);
    return { background: bg, color: readableOn(bg), borderBottom: '1px solid transparent' };
  }
  if (cfg.announcementStyle === 'near-black') {
    return { background: STOREFRONT_DARK, color: '#ededed', borderBottom: '1px solid transparent' };
  }
  return { background: '#f5f6f6', color: STOREFRONT_INK, borderBottom: '1px solid #cdd1d4' };
};

/* The Style tab's three personalities. Style sets the HEADLINE font only — the
   accent colour is chosen separately and applies to every style. */
const STYLE_CLASSES = ['classic', 'modern', 'friendly'];
const LEGACY_STYLES = { warm: 'classic', clean: 'modern', bold: 'friendly' };

/**
 * Write the partner's skin onto an element and return the resolved values.
 *
 * `config` is the storefront's saved `page_data.colorAndApperances`, which
 * stores the accent as a hex (`colorTheme`) and the header as a switch
 * (`headerStyle`), so there is no colour-key lookup to do here.
 *
 * @param {HTMLElement} el      the storefront root
 * @param {object}      config  page_data.colorAndApperances
 */
/* ⛔ ONE COLOUR IN, EVERY TOKEN OUT — and one owner for the fallback, so a
   surface that cannot use a CSS variable (a third-party widget's own style
   options, e.g. Finix's hosted card form on the checkout payment step) still
   agrees with `--o` instead of carrying a second, driftable copy of it. */
export const getAccent = (config) => String((config || {}).colorTheme || '').trim() || '#2f5d50';

export function applyStorefrontTheme(el, config) {
  if (!el) return null;
  const cfg = config || {};

  const style = LEGACY_STYLES[cfg.style] || cfg.style || 'classic';
  const styleClass = STYLE_CLASSES.indexOf(style) < 0 ? 'classic' : style;
  el.className = `sfx sfx-${styleClass}`;

  /* One colour in, every token out: the partner picks the accent and everything
     a customer sees is derived from it. */
  const accent = String(cfg.colorTheme || '').trim() || '#2f5d50';
  const accentInk = readableOn(accent);
  el.style.setProperty('--o', accent);
  el.style.setProperty('--o-ink', accentInk);
  // A light color uses its darker tone for links and button edges; a dark color keeps the stylesheet's `--o-text`.
  const tone = lightToneOf(accent);
  if (tone) el.style.setProperty('--o-text', tone);
  else el.style.removeProperty('--o-text');
  el.style.setProperty('--o-edge', tone || 'transparent');

  /* The header is a SWITCH, not a colour: white or the warm near-black, so the
     nav can never be the thing that fails contrast. */
  const headerBg = String(cfg.headerStyle || 'white') === 'near-black'
    ? STOREFRONT_DARK
    : '#ffffff';
  el.style.setProperty('--m', headerBg);
  el.style.setProperty('--m-ink', readableOn(headerBg));

  /* A white header needs an edge, or white nav on a white page has no boundary.
     A SHADOW, so it takes no layout space; transparent when the header is dark,
     which separates itself. */
  el.style.setProperty(
    '--m-edge',
    isDarkColor(headerBg) ? 'transparent' : 'rgba(36,31,29,.14)'
  );

  return { accent, accentInk, headerBg, styleClass };
}

/* useStorefrontTheme — the only correct way to attach the skin.
   Returns a ref for the themed root:
   `<div ref={useStorefrontTheme(cfg)} className="sfx">`.

   ⛔ Do not go back to `useRef` + `useEffect([appearance])`. A ref object
   mutating does not re-run an effect, so on any page whose themed root is behind
   a loading branch (the product page is) the order was a race: if `appearance`
   arrived before the product, the effect fired while the root was unmounted,
   `applyStorefrontTheme(null)` returned early, and nothing re-applied it — the
   page rendered in the wrong accent until some later context update changed
   `appearance` by identity. That is why the colour "changed on refresh".

   A callback ref fixes it because attaching the node is a state change, so the
   effect re-runs with both node and config. `useLayoutEffect`, not `useEffect`,
   so the tokens are written before paint. Detach passes null, which
   `applyStorefrontTheme` already no-ops on. */
export function useStorefrontTheme(config) {
  const [el, setEl] = useState(null);
  useLayoutEffect(() => {
    applyStorefrontTheme(el, config);
  }, [el, config]);
  return setEl;
}

export default applyStorefrontTheme;
